import type { RouteRecordRaw } from 'vue-router';

/**
 * Finance web screens (M3 WP3.6, DECISIONS.md ADR-050): setup (chart of accounts, fiscal
 * periods, dimensions, currencies), journals, posting profiles/inventory postings, tax/AR-AP/
 * cash-bank and reports. Kept in its own file per the parallel-work convention established by
 * `inventory.ts`/`textile.ts` — `router.ts` only spreads this array into the app-shell `children`.
 */
export const financeRoutes: RouteRecordRaw[] = [
  // ---- Setup ------------------------------------------------------------------------------------
  {
    path: 'finance/accounts',
    name: 'finance-accounts',
    component: () => import('../pages/finance/setup/ChartOfAccountsPage.vue'),
    meta: { requiresModule: 'finance' },
  },
  {
    path: 'finance/periods',
    name: 'finance-periods',
    component: () => import('../pages/finance/setup/FiscalPeriodsPage.vue'),
    meta: { requiresModule: 'finance' },
  },
  {
    path: 'finance/dimensions',
    name: 'finance-dimensions',
    component: () => import('../pages/finance/setup/DimensionsPage.vue'),
    meta: { requiresModule: 'finance' },
  },
  {
    path: 'finance/currencies',
    name: 'finance-currencies',
    component: () => import('../pages/finance/setup/CurrenciesPage.vue'),
    meta: { requiresModule: 'finance' },
  },
  // ---- Journals -----------------------------------------------------------------------------------
  {
    path: 'finance/journals',
    name: 'finance-journals',
    component: () => import('../pages/finance/journals/JournalsPage.vue'),
    meta: { requiresModule: 'finance' },
  },
  {
    path: 'finance/journals/new',
    name: 'finance-journal-new',
    component: () => import('../pages/finance/journals/CreateJournalPage.vue'),
    meta: { requiresModule: 'finance' },
  },
  {
    path: 'finance/journals/:id',
    name: 'finance-journal-detail',
    component: () => import('../pages/finance/journals/JournalDetailPage.vue'),
    props: true,
    meta: { requiresModule: 'finance' },
  },
  // ---- Posting profiles & inventory postings -------------------------------------------------------
  {
    path: 'finance/posting-profiles',
    name: 'finance-posting-profiles',
    component: () => import('../pages/finance/posting/PostingProfilesPage.vue'),
    meta: { requiresModule: 'finance' },
  },
  {
    path: 'finance/postings',
    name: 'finance-postings',
    component: () => import('../pages/finance/posting/PostingsInboxPage.vue'),
    meta: { requiresModule: 'finance' },
  },
  {
    path: 'finance/reconciliation',
    name: 'finance-reconciliation',
    component: () => import('../pages/finance/posting/ReconciliationPage.vue'),
    meta: { requiresModule: 'finance' },
  },
  // ---- Tax, AR/AP, cash/bank -----------------------------------------------------------------------
  {
    path: 'finance/tax-codes',
    name: 'finance-tax-codes',
    component: () => import('../pages/finance/tax/TaxCodesPage.vue'),
    meta: { requiresModule: 'finance' },
  },
  {
    path: 'finance/arap/documents',
    name: 'finance-arap-documents',
    component: () => import('../pages/finance/arap/ArApDocumentsPage.vue'),
    meta: { requiresModule: 'finance' },
  },
  {
    path: 'finance/arap/documents/new/:docType',
    name: 'finance-arap-document-new',
    component: () => import('../pages/finance/arap/CreateArApDocumentPage.vue'),
    props: true,
    meta: { requiresModule: 'finance' },
  },
  {
    path: 'finance/arap/documents/:id',
    name: 'finance-arap-document-detail',
    component: () => import('../pages/finance/arap/ArApDocumentDetailPage.vue'),
    props: true,
    meta: { requiresModule: 'finance' },
  },
  {
    path: 'finance/payments',
    name: 'finance-payments',
    component: () => import('../pages/finance/payments/PaymentsPage.vue'),
    meta: { requiresModule: 'finance' },
  },
  {
    path: 'finance/payments/new',
    name: 'finance-payment-new',
    component: () => import('../pages/finance/payments/CreatePaymentPage.vue'),
    meta: { requiresModule: 'finance' },
  },
  {
    path: 'finance/payments/:id',
    name: 'finance-payment-detail',
    component: () => import('../pages/finance/payments/PaymentDetailPage.vue'),
    props: true,
    meta: { requiresModule: 'finance' },
  },
  {
    path: 'finance/bank/accounts',
    name: 'finance-bank-accounts',
    component: () => import('../pages/finance/bank/BankAccountsPage.vue'),
    meta: { requiresModule: 'finance' },
  },
  {
    path: 'finance/bank/accounts/:id',
    name: 'finance-bank-account-detail',
    component: () => import('../pages/finance/bank/BankAccountDetailPage.vue'),
    props: true,
    meta: { requiresModule: 'finance' },
  },
  // ---- Reports ------------------------------------------------------------------------------------
  {
    path: 'finance/reports/trial-balance',
    name: 'finance-report-trial-balance',
    component: () => import('../pages/finance/reports/TrialBalancePage.vue'),
    meta: { requiresModule: 'finance' },
  },
  {
    path: 'finance/reports/general-ledger',
    name: 'finance-report-general-ledger',
    component: () => import('../pages/finance/reports/GeneralLedgerPage.vue'),
    meta: { requiresModule: 'finance' },
  },
  {
    path: 'finance/reports/account-statement',
    name: 'finance-report-account-statement',
    component: () => import('../pages/finance/reports/AccountStatementPage.vue'),
    meta: { requiresModule: 'finance' },
  },
  {
    path: 'finance/reports/balance-sheet',
    name: 'finance-report-balance-sheet',
    component: () => import('../pages/finance/reports/BalanceSheetPage.vue'),
    meta: { requiresModule: 'finance' },
  },
  {
    path: 'finance/reports/profit-and-loss',
    name: 'finance-report-profit-and-loss',
    component: () => import('../pages/finance/reports/ProfitAndLossPage.vue'),
    meta: { requiresModule: 'finance' },
  },
  {
    path: 'finance/reports/journal-register',
    name: 'finance-report-journal-register',
    component: () => import('../pages/finance/reports/JournalRegisterPage.vue'),
    meta: { requiresModule: 'finance' },
  },
  {
    path: 'finance/reports/open-items',
    name: 'finance-report-open-items',
    component: () => import('../pages/finance/reports/OpenItemsPage.vue'),
    meta: { requiresModule: 'finance' },
  },
  {
    path: 'finance/reports/party-statement',
    name: 'finance-report-party-statement',
    component: () => import('../pages/finance/reports/PartyStatementPage.vue'),
    meta: { requiresModule: 'finance' },
  },
  {
    path: 'finance/reports/aging',
    name: 'finance-report-aging',
    component: () => import('../pages/finance/reports/AgingPage.vue'),
    meta: { requiresModule: 'finance' },
  },
];
